import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
  Sparkles, Plus, Trash2, Edit2, ArrowLeft, 
  Loader2, Calendar, Clock, MapPin, Save, Star, Orbit,
  Users, User, Heart, Baby, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import AstrologyChat from '../components/AstrologyChat';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

// Relationship icons mapping
const RELATIONSHIP_ICONS = {
  self: Crown,
  spouse: Heart,
  child: Baby,
  parent: Users,
  sibling: User,
  other: Star
};

const RELATIONSHIP_COLORS = {
  self: 'from-[#C84B32] to-[#E85D43]',
  spouse: 'from-[#E91E63] to-[#F48FB1]',
  child: 'from-[#00BCD4] to-[#80DEEA]',
  parent: 'from-[#9C27B0] to-[#CE93D8]',
  sibling: 'from-[#4CAF50] to-[#A5D6A7]',
  other: 'from-[#FF9800] to-[#FFCC80]'
};

const RELATIONSHIP_LABELS = {
  self: 'Self',
  spouse: 'Spouse/Partner',
  child: 'Child',
  parent: 'Parent',
  sibling: 'Sibling',
  other: 'Other Family'
};

export const ProfilesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deletingProfile, setDeletingProfile] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [chatProfileId, setChatProfileId] = useState(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const containerRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    time_of_birth: '',
    place_of_birth: '',
    relationship: 'self'
  });

  // Time picker state (12-hour format)
  const [timeHour, setTimeHour] = useState('12');
  const [timeMinute, setTimeMinute] = useState('00');
  const [timePeriod, setTimePeriod] = useState('AM');

  // Convert 12-hour to 24-hour format for storage
  const updateTimeOfBirth = (hour, minute, period) => {
    let h = parseInt(hour);
    if (period === 'AM') {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h = h + 12;
    }
    const time24 = `${h.toString().padStart(2, '0')}:${minute}`;
    setFormData(prev => ({ ...prev, time_of_birth: time24 }));
  };

  // Parse 24-hour time to 12-hour components (for editing)
  const parseTimeFor12Hour = (time24) => {
    if (!time24) return { hour: '12', minute: '00', period: 'AM' };
    const [h, m] = time24.split(':');
    let hour = parseInt(h);
    let period = 'AM';
    if (hour >= 12) {
      period = 'PM';
      if (hour > 12) hour = hour - 12;
    } else if (hour === 0) {
      hour = 12;
    }
    return { 
      hour: hour.toString().padStart(2, '0'), 
      minute: m || '00', 
      period 
    };
  };

  useEffect(() => {
    // Check if user was redirected as a first-time user
    if (location.state?.firstTimeUser) {
      setIsFirstTimeUser(true);
      setIsDialogOpen(true); // Auto-open the create profile dialog
    }
    fetchProfiles();
  }, [location.state]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles`, { withCredentials: true });
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProfileLimit = () => {
    const tier = user?.subscription_tier || 'free';
    const limits = { free: 2, basic: 5, premium: 10 };
    return limits[tier] || 2;
  };

  const openAddDialog = (relationship = 'other') => {
    setEditingProfile(null);
    setFormData({
      name: '',
      date_of_birth: '',
      time_of_birth: '',
      place_of_birth: '',
      relationship: relationship
    });
    setTimeHour('12');
    setTimeMinute('00');
    setTimePeriod('AM');
    setError('');
    setIsDialogOpen(true);
  };

  const openEditDialog = (profile) => {
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      date_of_birth: profile.date_of_birth,
      time_of_birth: profile.time_of_birth,
      place_of_birth: profile.place_of_birth,
      relationship: profile.relationship || 'other'
    });
    // Parse existing time to 12-hour format
    const { hour, minute, period } = parseTimeFor12Hour(profile.time_of_birth);
    setTimeHour(hour);
    setTimeMinute(minute);
    setTimePeriod(period);
    setError('');
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (profile) => {
    setDeletingProfile(profile);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    try {
      if (editingProfile) {
        await axios.put(
          `${API}/profiles/${editingProfile.profile_id}`,
          formData,
          { withCredentials: true }
        );
      } else {
        await axios.post(`${API}/profiles`, formData, { withCredentials: true });
      }
      await fetchProfiles();
      setIsDialogOpen(false);
      setSelectedProfile(null);
      
      // If first-time user just created their first profile, redirect to dashboard
      if (isFirstTimeUser) {
        setIsFirstTimeUser(false);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingProfile) return;
    
    try {
      await axios.delete(`${API}/profiles/${deletingProfile.profile_id}`, { 
        withCredentials: true 
      });
      await fetchProfiles();
      setIsDeleteDialogOpen(false);
      setDeletingProfile(null);
      setSelectedProfile(null);
    } catch (error) {
      console.error('Error deleting profile:', error);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    let hour = parseInt(h);
    let period = 'AM';
    if (hour >= 12) {
      period = 'PM';
      if (hour > 12) hour = hour - 12;
    } else if (hour === 0) {
      hour = 12;
    }
    return `${hour}:${m} ${period}`;
  };

  // Get profiles organized by relationship
  const getOrganizedProfiles = () => {
    const organized = {
      self: profiles.filter(p => p.relationship === 'self'),
      spouse: profiles.filter(p => p.relationship === 'spouse'),
      parent: profiles.filter(p => p.relationship === 'parent'),
      child: profiles.filter(p => p.relationship === 'child'),
      sibling: profiles.filter(p => p.relationship === 'sibling'),
      other: profiles.filter(p => !p.relationship || p.relationship === 'other')
    };
    return organized;
  };

  const organizedProfiles = getOrganizedProfiles();

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
        <div className="text-center relative z-10">
          <Orbit className="w-12 h-12 text-[#C84B32] animate-spin mx-auto mb-4" />
          <p className="text-[#6B6560]">Loading your family constellation...</p>
        </div>
      </div>
    );
  }

  // Profile Node Component
  const ProfileNode = ({ profile, size = 'medium', showActions = true }) => {
    const Icon = RELATIONSHIP_ICONS[profile.relationship] || Star;
    const colorClass = RELATIONSHIP_COLORS[profile.relationship] || RELATIONSHIP_COLORS.other;
    const isSelected = selectedProfile?.profile_id === profile.profile_id;
    
    const sizeClasses = {
      small: 'w-16 h-16',
      medium: 'w-20 h-20 md:w-24 md:h-24',
      large: 'w-24 h-24 md:w-28 md:h-28'
    };

    return (
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        whileHover={{ scale: 1.05 }}
        className="flex flex-col items-center group cursor-pointer"
        onClick={() => setSelectedProfile(isSelected ? null : profile)}
        data-testid={`profile-node-${profile.profile_id}`}
      >
        {/* Glow ring */}
        <div className={`relative ${sizeClasses[size]} transition-all duration-300 ${isSelected ? 'scale-110' : ''}`}>
          {/* Outer glow */}
          <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${colorClass} opacity-20 blur-xl group-hover:opacity-40 transition-opacity ${isSelected ? 'opacity-50' : ''}`} />
          
          {/* Orbit ring */}
          <div className={`absolute -inset-2 rounded-full border border-dashed ${isSelected ? 'border-[#C84B32]/50' : 'border-[#282622]/10'} group-hover:border-[#282622]/15 transition-colors animate-spin-slow`} />
          
          {/* Profile circle */}
          <div className={`relative ${sizeClasses[size]} rounded-full bg-gradient-to-br ${colorClass} flex items-center justify-center shadow-lg shadow-black/30 ring-2 ${isSelected ? 'ring-[#C84B32]' : 'ring-white/10'} group-hover:ring-white/30 transition-all`}>
            <span className="text-white font-bold text-lg md:text-xl">
              {getInitials(profile.name)}
            </span>
          </div>
          
          {/* Relationship icon badge */}
          <div className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border-2 border-[#282622]/15 flex items-center justify-center`}>
            <Icon className="w-3.5 h-3.5 text-[#C84B32]" />
          </div>
        </div>
        
        {/* Name */}
        <span className="mt-3 text-[#282622] text-sm font-medium text-center max-w-[100px] truncate">
          {profile.name}
        </span>
        <span className="text-[#6B6560] text-xs">
          {RELATIONSHIP_LABELS[profile.relationship] || 'Family'}
        </span>
      </motion.div>
    );
  };

  // Add Profile Node Component
  const AddProfileNode = ({ relationship, label }) => {
    const canAdd = profiles.length < getProfileLimit();
    const Icon = RELATIONSHIP_ICONS[relationship] || Plus;
    
    return (
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: canAdd ? 1.1 : 1 }}
        whileTap={{ scale: canAdd ? 0.95 : 1 }}
        onClick={() => canAdd && openAddDialog(relationship)}
        disabled={!canAdd}
        data-testid={`add-profile-${relationship}`}
        className={`flex flex-col items-center group ${!canAdd ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="relative w-16 h-16 md:w-20 md:h-20 transition-all">
          {/* Dashed border circle */}
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#C84B32]/30 group-hover:border-[#C84B32]/60 transition-colors" />
          
          {/* Inner circle */}
          <div className="absolute inset-2 rounded-full bg-[#FFF9EF] group-hover:bg-[#C84B32]/10 flex items-center justify-center transition-colors">
            <Plus className="w-6 h-6 text-[#C84B32]/60 group-hover:text-[#C84B32] transition-colors" />
          </div>
        </div>
        
        <span className="mt-2 text-[#6B6560] text-xs group-hover:text-[#C84B32] transition-colors">
          {label}
        </span>
      </motion.button>
    );
  };

  return (
    <div className="min-h-screen cosmic-bg">
      {/* Background effects */}
      <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
      <div className="fixed inset-0 nebula-bg pointer-events-none" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#FFF9EF] backdrop-blur-xl border-b border-[#282622]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
                className="p-2 rounded-xl hover:bg-[#FCE9D9]/60 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#6B6560]" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <span className="font-['Fraunces'] text-xl md:text-2xl font-semibold text-[#282622]">Family Tree</span>
              </div>
            </div>
            
            <div className="text-right">
              <span className="text-[#C84B32] font-semibold">{profiles.length}</span>
              <span className="text-[#6B6560] text-sm">/{getProfileLimit()} profiles</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={containerRef}>
        
        {/* Family Tree Visualization */}
        <div className="cosmic-card p-6 md:p-10 mb-8">
          <div className="text-center mb-8">
            <h2 className="font-['Fraunces'] text-2xl md:text-3xl text-[#282622] mb-2">
              Your Cosmic Family
            </h2>
            <p className="text-[#6B6560] text-sm">
              Tap a profile to view details • Add family members to expand your constellation
            </p>
          </div>

          {/* Tree Layout */}
          <div className="relative">
            {/* SVG Connection Lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" style={{ zIndex: 0 }}>
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.1" />
                  <stop offset="50%" stopColor="#D4AF37" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.1" />
                </linearGradient>
              </defs>
            </svg>

            {/* Tree Rows */}
            <div className="relative z-10 space-y-12">
              
              {/* Parents Row */}
              {(organizedProfiles.parent.length > 0 || profiles.length < getProfileLimit()) && (
                <div className="flex justify-center">
                  <div className="flex items-end gap-8 md:gap-16">
                    <AnimatePresence>
                      {organizedProfiles.parent.map(profile => (
                        <ProfileNode key={profile.profile_id} profile={profile} size="medium" />
                      ))}
                    </AnimatePresence>
                    {organizedProfiles.parent.length < 2 && profiles.length < getProfileLimit() && (
                      <AddProfileNode relationship="parent" label="Add Parent" />
                    )}
                  </div>
                </div>
              )}

              {/* Connection line from parents to self */}
              {organizedProfiles.parent.length > 0 && (organizedProfiles.self.length > 0 || organizedProfiles.sibling.length > 0) && (
                <div className="flex justify-center">
                  <div className="w-px h-8 bg-gradient-to-b from-[#C84B32]/30 to-[#F5B942]/10" />
                </div>
              )}

              {/* Self & Siblings Row */}
              <div className="flex justify-center">
                <div className="flex items-center gap-6 md:gap-12">
                  {/* Siblings (left) */}
                  <AnimatePresence>
                    {organizedProfiles.sibling.slice(0, Math.ceil(organizedProfiles.sibling.length / 2)).map(profile => (
                      <ProfileNode key={profile.profile_id} profile={profile} size="medium" />
                    ))}
                  </AnimatePresence>

                  {/* Self (Center - Larger) */}
                  <div className="relative">
                    {organizedProfiles.self.length > 0 ? (
                      <AnimatePresence>
                        {organizedProfiles.self.map(profile => (
                          <ProfileNode key={profile.profile_id} profile={profile} size="large" />
                        ))}
                      </AnimatePresence>
                    ) : (
                      <motion.button
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openAddDialog('self')}
                        disabled={profiles.length >= getProfileLimit()}
                        data-testid="add-self-profile"
                        className="flex flex-col items-center group"
                      >
                        <div className="relative w-24 h-24 md:w-28 md:h-28">
                          {/* Pulsing glow */}
                          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] opacity-20 blur-xl animate-pulse" />
                          
                          {/* Dashed border */}
                          <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#C84B32]/50 animate-spin-slow" />
                          
                          {/* Inner content */}
                          <div className="absolute inset-3 rounded-full bg-white border border-[#C84B32]/30 flex flex-col items-center justify-center group-hover:bg-[#C84B32]/10 transition-colors">
                            <Crown className="w-6 h-6 text-[#C84B32] mb-1" />
                            <Plus className="w-5 h-5 text-[#C84B32]" />
                          </div>
                        </div>
                        <span className="mt-3 text-[#C84B32] text-sm font-medium">Add Yourself</span>
                        <span className="text-[#6B6560] text-xs">Start here</span>
                      </motion.button>
                    )}
                  </div>

                  {/* Spouse */}
                  {organizedProfiles.spouse.length > 0 ? (
                    <AnimatePresence>
                      {organizedProfiles.spouse.map(profile => (
                        <ProfileNode key={profile.profile_id} profile={profile} size="large" />
                      ))}
                    </AnimatePresence>
                  ) : (
                    organizedProfiles.self.length > 0 && profiles.length < getProfileLimit() && (
                      <AddProfileNode relationship="spouse" label="Add Spouse" />
                    )
                  )}

                  {/* Siblings (right) */}
                  <AnimatePresence>
                    {organizedProfiles.sibling.slice(Math.ceil(organizedProfiles.sibling.length / 2)).map(profile => (
                      <ProfileNode key={profile.profile_id} profile={profile} size="medium" />
                    ))}
                  </AnimatePresence>

                  {/* Add sibling button */}
                  {profiles.length < getProfileLimit() && organizedProfiles.self.length > 0 && (
                    <AddProfileNode relationship="sibling" label="Add Sibling" />
                  )}
                </div>
              </div>

              {/* Connection line to children */}
              {(organizedProfiles.self.length > 0 || organizedProfiles.spouse.length > 0) && (organizedProfiles.child.length > 0 || profiles.length < getProfileLimit()) && (
                <div className="flex justify-center">
                  <div className="w-px h-8 bg-gradient-to-b from-[#C84B32]/30 to-[#F5B942]/10" />
                </div>
              )}

              {/* Children Row */}
              {(organizedProfiles.child.length > 0 || (organizedProfiles.self.length > 0 && profiles.length < getProfileLimit())) && (
                <div className="flex justify-center">
                  <div className="flex items-start gap-6 md:gap-12">
                    <AnimatePresence>
                      {organizedProfiles.child.map(profile => (
                        <ProfileNode key={profile.profile_id} profile={profile} size="medium" />
                      ))}
                    </AnimatePresence>
                    {profiles.length < getProfileLimit() && organizedProfiles.self.length > 0 && (
                      <AddProfileNode relationship="child" label="Add Child" />
                    )}
                  </div>
                </div>
              )}

              {/* Other Family Members */}
              {organizedProfiles.other.length > 0 && (
                <>
                  <div className="border-t border-[#282622]/10 pt-8 mt-8">
                    <p className="text-center text-[#6B6560] text-sm mb-6">Other Family Members</p>
                    <div className="flex justify-center flex-wrap gap-6 md:gap-10">
                      <AnimatePresence>
                        {organizedProfiles.other.map(profile => (
                          <ProfileNode key={profile.profile_id} profile={profile} size="small" />
                        ))}
                      </AnimatePresence>
                      {profiles.length < getProfileLimit() && (
                        <AddProfileNode relationship="other" label="Add Other" />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-10 pt-6 border-t border-[#282622]/10">
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              {Object.entries(RELATIONSHIP_LABELS).map(([key, label]) => {
                const Icon = RELATIONSHIP_ICONS[key];
                return (
                  <div key={key} className="flex items-center gap-2 text-xs text-[#6B6560]">
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${RELATIONSHIP_COLORS[key]} flex items-center justify-center`}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Selected Profile Details Panel */}
        <AnimatePresence>
          {selectedProfile && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="cosmic-card p-6 mb-8"
              data-testid="profile-details-panel"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div 
                    className={`w-14 h-14 rounded-full bg-gradient-to-br ${RELATIONSHIP_COLORS[selectedProfile.relationship] || RELATIONSHIP_COLORS.other} flex items-center justify-center shadow-lg`}
                  >
                    <span className="text-white font-bold text-lg">{getInitials(selectedProfile.name)}</span>
                  </div>
                  <div>
                    <h3 className="font-['Fraunces'] text-xl text-[#282622]">
                      {selectedProfile.name}
                    </h3>
                    <span className="text-[#6B6560] text-sm">
                      {RELATIONSHIP_LABELS[selectedProfile.relationship] || 'Family Member'}
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4 text-sm text-[#6B6560]">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#C84B32]" />
                    <span>{formatDate(selectedProfile.date_of_birth)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#C84B32]" />
                    <span>{formatTime12Hour(selectedProfile.time_of_birth)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#C84B32]" />
                    <span>{selectedProfile.place_of_birth}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => openEditDialog(selectedProfile)}
                    data-testid={`edit-selected-profile`}
                    variant="outline"
                    size="sm"
                    className="border-[#C84B32]/30 text-[#C84B32] hover:bg-[#C84B32]/10"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => openDeleteDialog(selectedProfile)}
                    data-testid={`delete-selected-profile`}
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-600 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Add Button (Mobile FAB) */}
        {profiles.length < getProfileLimit() && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => openAddDialog('other')}
            data-testid="fab-add-profile"
            className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-r from-[#C84B32] to-[#F5B942] shadow-lg shadow-[#C84B32]/30 flex items-center justify-center md:hidden z-40"
          >
            <Plus className="w-6 h-6 text-white" />
          </motion.button>
        )}

        {/* Empty State */}
        {profiles.length === 0 && !isFirstTimeUser && (
          <div className="cosmic-card p-10 text-center">
            <Star className="w-16 h-16 text-[#C84B32] mx-auto mb-6 animate-pulse" />
            <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-3">
              Begin Your Family Constellation
            </h3>
            <p className="text-[#6B6560] mb-6 max-w-md mx-auto">
              Create your first profile to start mapping your family's cosmic journey. Start with yourself, then add family members.
            </p>
            <Button
              onClick={() => openAddDialog('self')}
              data-testid="create-first-profile-empty"
              className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#E85D43] hover:to-[#F5B942] font-medium"
            >
              <Crown className="w-4 h-4 mr-2" />
              Add Yourself First
            </Button>
          </div>
        )}

        {/* Profile Limit Reached */}
        {profiles.length >= getProfileLimit() && (
          <div className="cosmic-card p-6 border border-[#C84B32]/20">
            <div className="flex items-center gap-3 mb-3">
              <Star className="w-5 h-5 text-[#C84B32]" />
              <p className="text-[#282622]">Family constellation limit reached</p>
            </div>
            <p className="text-[#6B6560] text-sm mb-4">
              Upgrade your plan to add more family members to your cosmic chart.
            </p>
            <Button
              onClick={() => navigate('/subscription')}
              data-testid="upgrade-for-profiles"
              variant="outline"
              className="border-[#C84B32]/50 text-[#C84B32] hover:bg-[#C84B32]/10"
            >
              Expand Your Universe
            </Button>
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        // Don't allow closing if first-time user without any profiles
        if (!open && isFirstTimeUser && profiles.length === 0) {
          return;
        }
        setIsDialogOpen(open);
      }}>
        <DialogContent className="bg-white border-[#282622]/10 text-[#282622] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Fraunces'] text-2xl md:text-3xl">
              {isFirstTimeUser && profiles.length === 0 
                ? 'Welcome to Nakshatra!' 
                : editingProfile ? 'Edit Profile' : 'Add Family Member'}
            </DialogTitle>
            <DialogDescription className="text-[#6B6560]">
              {isFirstTimeUser && profiles.length === 0 
                ? 'Create your profile to unlock personalized cosmic insights'
                : 'Enter birth details to chart the cosmic blueprint'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Relationship Selector */}
            <div className="space-y-2">
              <Label className="text-[#6B6560]">Relationship</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(RELATIONSHIP_LABELS).map(([key, label]) => {
                  const Icon = RELATIONSHIP_ICONS[key];
                  const isSelected = formData.relationship === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData({ ...formData, relationship: key })}
                      data-testid={`relationship-${key}`}
                      className={`p-3 rounded-xl border transition-all flex flex-col items-center gap-1 ${
                        isSelected 
                          ? 'border-[#C84B32] bg-[#C84B32]/10' 
                          : 'border-[#282622]/10 hover:border-[#282622]/15 bg-white'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-[#C84B32]' : 'text-[#6B6560]'}`} />
                      <span className={`text-xs ${isSelected ? 'text-[#C84B32]' : 'text-[#6B6560]'}`}>
                        {label.split('/')[0]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#6B6560]">Full Name</Label>
              <Input
                id="name"
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="profile-name-input"
                className="bg-white border-[#282622]/10 text-[#282622] focus:border-[#C84B32] h-12 rounded-xl"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dob" className="text-[#6B6560]">Date of Birth</Label>
              <Input
                id="dob"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                data-testid="profile-dob-input"
                className="bg-white border-[#282622]/10 text-[#282622] focus:border-[#C84B32] h-12 rounded-xl"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tob" className="text-[#6B6560]">Time of Birth</Label>
              <div className="flex gap-2">
                {/* Hour Select */}
                <select
                  value={timeHour}
                  onChange={(e) => {
                    setTimeHour(e.target.value);
                    updateTimeOfBirth(e.target.value, timeMinute, timePeriod);
                  }}
                  data-testid="profile-tob-hour"
                  className="flex-1 bg-white border border-[#282622]/10 text-[#282622] focus:border-[#C84B32] h-12 rounded-xl px-3 outline-none appearance-none cursor-pointer"
                  required
                >
                  {[...Array(12)].map((_, i) => {
                    const hour = (i + 1).toString().padStart(2, '0');
                    return <option key={hour} value={hour}>{hour}</option>;
                  })}
                </select>
                
                <span className="text-[#C84B32] text-2xl flex items-center">:</span>
                
                {/* Minute Select */}
                <select
                  value={timeMinute}
                  onChange={(e) => {
                    setTimeMinute(e.target.value);
                    updateTimeOfBirth(timeHour, e.target.value, timePeriod);
                  }}
                  data-testid="profile-tob-minute"
                  className="flex-1 bg-white border border-[#282622]/10 text-[#282622] focus:border-[#C84B32] h-12 rounded-xl px-3 outline-none appearance-none cursor-pointer"
                  required
                >
                  {[...Array(60)].map((_, i) => {
                    const minute = i.toString().padStart(2, '0');
                    return <option key={minute} value={minute}>{minute}</option>;
                  })}
                </select>
                
                {/* AM/PM Select */}
                <select
                  value={timePeriod}
                  onChange={(e) => {
                    setTimePeriod(e.target.value);
                    updateTimeOfBirth(timeHour, timeMinute, e.target.value);
                  }}
                  data-testid="profile-tob-period"
                  className="w-20 bg-gradient-to-r from-[#C84B32]/20 to-[#F5B942]/20 border border-[#C84B32]/30 text-[#C84B32] font-semibold h-12 rounded-xl px-2 outline-none appearance-none cursor-pointer text-center"
                  required
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              <p className="text-xs text-[#6B6560]/60">Select hour, minute, and AM/PM</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pob" className="text-[#6B6560]">Place of Birth</Label>
              <Input
                id="pob"
                placeholder="City, Country"
                value={formData.place_of_birth}
                onChange={(e) => setFormData({ ...formData, place_of_birth: e.target.value })}
                data-testid="profile-pob-input"
                className="bg-white border-[#282622]/10 text-[#282622] focus:border-[#C84B32] h-12 rounded-xl"
                required
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => setIsDialogOpen(false)}
                variant="outline"
                disabled={isFirstTimeUser && profiles.length === 0}
                className="flex-1 border-[#282622]/10 text-[#6B6560] hover:bg-[#FCE9D9]/60 h-12 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={formLoading}
                data-testid="save-profile-btn"
                className="flex-1 bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#E85D43] hover:to-[#F5B942] h-12 rounded-xl font-medium"
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-[#282622]/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#282622] font-['Fraunces'] text-2xl">Remove from Family Tree</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B6560]">
              Are you sure you want to remove "{deletingProfile?.name}" from your family constellation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#282622]/10 text-[#6B6560] hover:bg-[#FCE9D9]/60 hover:text-[#282622] rounded-xl">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
              className="bg-red-500 text-white hover:bg-red-600 rounded-xl"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Astrology Chat */}
      <AstrologyChat 
        profiles={profiles}
        selectedProfileId={chatProfileId}
        onSelectProfile={setChatProfileId}
      />
    </div>
  );
};

export default ProfilesPage;
